import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    AgeingBar, Button, Card, EmptyState, IconDownload, LoadingList, PageHeader, Panel, ProportionBar, RowHead, SegmentBar, Table, Td, Th,
    ageSwatch, cx, fieldClass, filterLabelClass, useIsPhone,
} from '@timely/ui';
import {
    DEFAULT_FILTERS, KIND_CAPTION, REPORT_PRESETS, UNPLANNED_QUERY, worklistQuery,
    concentration, followUpCoverage, holds, percentWords, periodFromParams, periodLength, csvFileName,
    type ReportPeriod, type ReportPreset,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { useFormatters } from '../book/useFormatters';
import { DateField } from '../forms/DateField';
import {
    ExportError, downloadCsv, exportCustomers, useReports,
    type ActivityTotals, type AgeingRow, type MemberReport, type PromiseOutcomes, type Receivables,
} from '../reports/useReports';
import { paths } from '../workspace/navigation';
import { SourceFreshness } from '../receivables/SourceFreshness';
import { agedRows, ageingRowLabel, besideRows } from '../receivables/ageingReport';
import { useLargestAccounts } from '../book/useBook';

/**
 * Reports: what the company is owed, and what its people did about it.
 *
 * Three rules shape it, and they are the same three that shaped Today.
 *
 * **Every figure is a door.** A number nobody can open is a number nobody can
 * check, and a report full of them is decoration. Each one that has a matching
 * customer set links to Customers filtered to exactly the accounts it counted —
 * the ageing rows carry the filter value the database counted them by, so the
 * two cannot drift.
 *
 * **Current state and period activity are kept apart**, in separate panels
 * with captions saying which is which. They are different kinds of fact, and a
 * screen that mixes them is a screen where somebody changes the month and
 * wonders why the outstanding figure did not move. The period picker sits in
 * the header of the one panel it changes (PRODUCT-UX-FOUNDATION §10).
 *
 * **An aggregate is as much of a disclosure as a row.** Every figure comes from
 * a `security invoker` function, so a collector's report is of their own
 * accounts. The team section needs `reports.view.all`; the export needs
 * `reports.export`, which is a right of its own — a collector may read their
 * own report and may not take it out of the building.
 *
 * Every table here is the shared table (DESIGN-SYSTEM §9): figures right-aligned
 * in tabular numerals, first and last columns inset to the panel's heading.
 */
export function Reports() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const { count, date, today } = useFormatters();
    const isPhone = useIsPhone();

    const canSeeAll = holds(context, 'reports.view.all');
    const canExport = holds(context, 'reports.export');
    const title = canSeeAll ? 'Reports' : 'My reports';
    const subtitle = canSeeAll
        ? 'Where the money is, and what is being done about it.'
        : 'Where the money on your accounts is, and what you did about it.';

    const [params, setParams] = useSearchParams();
    const period = periodFromParams(params, today);
    // Whose accounts the report covers (0033), for somebody who sees everyone's.
    const owner = canSeeAll ? (params.get('owner') || 'any') : 'any';

    const { receivables, ageing, activity, promises, promisesAwaiting, team, teamUnavailable, loading, error, reload } =
        useReports(context, period, { withTeam: canSeeAll, owner });
    const largest = useLargestAccounts(workspace.id, owner);

    const [exporting, setExporting] = useState(false);
    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

    // Every figure opens the accounts it counted — narrowed to the same person when the report is.
    const book = (query: string) => {
        const next = new URLSearchParams(query);
        if (owner !== 'any' && !next.has('owner')) next.set('owner', owner);
        const text = next.toString();
        return `${paths(workspace.slug).customers}${text ? `?${text}` : ''}`;
    };
    const ownerName = owner === 'unassigned' ? 'nobody'
        : team.find(m => m.memberId === owner)?.displayName ?? null;

    const setPeriod = (changes: { period?: ReportPreset; from?: string; to?: string }) => {
        const next = new URLSearchParams(params);
        for (const [key, value] of Object.entries(changes)) {
            if (!value) next.delete(key); else next.set(key, value);
        }
        setParams(next, { replace: true });
    };

    const runExport = async () => {
        if (exporting) return;
        setExporting(true);
        setNotice(null);
        try {
            // The filters Customers would use for "everything with dues" — the
            // same question, answered again by the database under this
            // person's own authorisation.
            const result = await exportCustomers(
                workspace.id, { ...DEFAULT_FILTERS, owner }, workspace.currency,
            );
            if (result.rowCount === 0) {
                setNotice({ tone: 'error', text: 'There is nothing to export.' });
                return;
            }
            downloadCsv(
                csvFileName('customers', workspace.name, date.todayValue()),
                result.csv,
            );
            setNotice({
                tone: 'ok',
                text: result.capped
                    ? `Exported the largest ${count(result.rowCount)} accounts. Narrow the list in Customers and export again for the rest.`
                    : `Exported ${count(result.rowCount)} accounts.`,
            });
        } catch (failed) {
            setNotice({
                tone: 'error',
                text: failed instanceof ExportError ? failed.message : 'That export did not work.',
            });
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <Page width="wide">
                <PageHeader title={title} subtitle={subtitle} />
                <Card className="overflow-hidden"><LoadingList label="Building your report" rows={5} /></Card>
            </Page>
        );
    }

    if (error) {
        return (
            <Page width="wide">
                <PageHeader title={title} subtitle={subtitle} />
                <FormNotice tone="error">{error}</FormNotice>
                <div className="mt-4">
                    <Button variant="secondary" onClick={() => { void reload(); }}>Try again</Button>
                </div>
            </Page>
        );
    }

    // A company with no receivables has not started, and a page of zeroes
    // would be a dozen ways of saying so badly.
    if (!receivables || (receivables.withDuesCount === 0 && receivables.settledCount === 0)) {
        return (
            <Page width="wide">
                <PageHeader title={title} subtitle={subtitle} />
                <Card>
                    <EmptyState
                        title="No receivables yet"
                        hint={holds(context, 'datasource.manage')
                            ? 'Connect your receivables data — TallyPrime or a Google Sheet — and every figure here fills in.'
                            : 'Once somebody connects your receivables data, this fills in.'}
                        action={holds(context, 'datasource.manage')
                            ? (
                                <Link to={paths(workspace.slug).setting('data')} className="text-accent-text font-semibold hover:underline">
                                    Connect your receivables data
                                </Link>
                            )
                            : undefined}
                    />
                </Card>
            </Page>
        );
    }

    return (
        <Page width="wide">
            <PageHeader
                title={title}
                subtitle={subtitle}
                actions={(canSeeAll || canExport) ? (
                    <div className="flex items-end gap-2.5 flex-wrap justify-end">
                        {canSeeAll && team.length > 0 && (
                            <div>
                                <label htmlFor="report-owner" className={filterLabelClass}>Whose accounts</label>
                                <select
                                    id="report-owner"
                                    value={owner}
                                    onChange={e => {
                                        const next = new URLSearchParams(params);
                                        if (e.target.value === 'any') next.delete('owner'); else next.set('owner', e.target.value);
                                        setParams(next, { replace: true });
                                    }}
                                    className={fieldClass({ className: cx('min-w-44', owner !== 'any' && '!border-accent bg-accent-tint') })}
                                >
                                    <option value="any">Everyone’s</option>
                                    {team.map(m => <option key={m.memberId} value={m.memberId}>{m.displayName}</option>)}
                                    <option value="unassigned">Nobody’s (unassigned)</option>
                                </select>
                            </div>
                        )}
                        {canExport && (
                            <Button variant="secondary" disabled={exporting} onClick={() => { void runExport(); }} busy={exporting}>
                                <IconDownload size={14} />{exporting ? 'Building…' : 'Export accounts'}
                            </Button>
                        )}
                    </div>
                ) : undefined}
            />

            {/* How old the position below is: a report of old figures must say so (Phase 0). */}
            <SourceFreshness className="-mt-2 mb-4" />

            {notice && <div className="mb-4"><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}
            {owner !== 'any' && (
                <p className="mb-4 text-secondary text-label-2" role="status">
                    Showing the accounts {ownerName ? (owner === 'unassigned' ? 'assigned to nobody' : `of ${ownerName}`) : 'of one person'}, as owner or collector.
                    What was done counts everything recorded on those accounts, by anyone.
                </p>
            )}

            {/* Now: the position and its age, side by side where there is room; both take the taller one's height, so
                the pair ends on one line (2026-10 global sweep). Where it concentrates has a row of its own beneath:
                beside the age it left the position's panel a long empty foot (Customer 360, 02 §8, as built). */}
            <div className="grid gap-6 xl:grid-cols-2">
                <Panel title="Where the money is" description={KIND_CAPTION.current}>
                    <Position receivables={receivables} book={book} />
                </Panel>
                <Panel
                    title="How old the money is"
                    description="Debit balances by how long they have been owed: an age, not how late anything is. Open a band to see its accounts."
                >
                    <Ageing rows={ageing} receivable={receivables.receivable} book={book} isPhone={isPhone} />
                </Panel>
            </div>
            {largest && <Concentration largest={largest} receivable={receivables.receivable} book={book} slug={workspace.slug} />}

            {/* The period: its picker, and everything the picker changes. */}
            <Panel
                className="mt-6"
                title="What was done"
                description={
                    <>
                        {KIND_CAPTION.period} {date.day(`${period.from}T12:00:00`)} to{' '}
                        {date.day(`${period.to}T12:00:00`)} · {count(periodLength(period))}{' '}
                        {periodLength(period) === 1 ? 'day' : 'days'}.
                    </>
                }
                actions={<PeriodPicker period={period} onChange={setPeriod} />}
            >
                <Activity activity={activity} slug={workspace.slug} promises={promises} awaiting={promisesAwaiting} book={book} />
                {canSeeAll && owner === 'any' && (
                    <Team team={team} unavailable={teamUnavailable} book={book} isPhone={isPhone} />
                )}
            </Panel>
        </Page>
    );
}

/* -------------------------------- the period ------------------------------ */

/**
 * The period, as a filter on the panel it governs: a labelled select, and two
 * day fields when the period is one somebody chose.
 */
function PeriodPicker({
    period, onChange,
}: {
    period: ReportPeriod;
    onChange: (changes: { period?: ReportPreset; from?: string; to?: string }) => void;
}) {
    return (
        <div className="flex items-end gap-2.5 flex-wrap">
            <div>
                <label htmlFor="report-period" className={filterLabelClass}>Period</label>
                <select
                    id="report-period"
                    value={period.preset}
                    onChange={e => onChange({ period: e.target.value as ReportPreset })}
                    className={fieldClass({ className: 'min-w-40' })}
                >
                    {REPORT_PRESETS.map(preset => (
                        <option key={preset.preset} value={preset.preset} title={preset.hint}>
                            {preset.label}
                        </option>
                    ))}
                </select>
            </div>
            {period.preset === 'custom' && (
                <>
                    <div className="w-48">
                        <DateField id="report-from" label="From" value={period.from}
                            onChange={value => onChange({ period: 'custom', from: value })} />
                    </div>
                    <div className="w-48">
                        <DateField id="report-to" label="To" value={period.to} notBefore={period.from}
                            onChange={value => onChange({ period: 'custom', to: value })} />
                    </div>
                </>
            )}
        </div>
    );
}

/* ------------------------------ the position ------------------------------ */

type Tone = 'neutral' | 'dang' | 'warn' | 'pos';
const TONE: Record<Tone, string> = {
    neutral: 'text-label', dang: 'text-dang', warn: 'text-warn', pos: 'text-pos',
};

/**
 * One line of the position: how many accounts, how much money, and — when
 * Customers has a filter for exactly this set — the way in.
 *
 * A line with no `to` has no matching customer list — the credit total, for
 * instance, where Customers has no "in credit" filter. It stays plain text
 * rather than a link that goes somewhere approximate.
 */
interface PositionLine {
    label: string;
    sub?: string;
    accounts: number;
    amount?: number;
    to?: string;
    tone?: Tone;
}

/**
 * Where the money is, now: the total owed, then one table of the states that
 * matter, each with its account count and amount in aligned columns. Eight
 * separate cards said the same thing in more space and could not be compared
 * down a column.
 */
function Position({
    receivables: r, book,
}: {
    receivables: Receivables;
    book: (query: string) => string;
}) {
    const { money, count } = useFormatters();

    const lines: PositionLine[] = [
        {
            // Late: their follow-up date has gone by. What they owe is their balance, not money past its due date (F1, F6).
            label: 'Late follow-ups', accounts: r.overdueCount, amount: r.overdueAmount,
            tone: r.overdueCount > 0 ? 'warn' : 'neutral', to: book(worklistQuery('overdue')),
        },
        { label: 'Due today', accounts: r.dueTodayCount, to: book(worklistQuery('today')) },
        {
            label: 'No follow-up set', sub: 'Money owed and nothing planned', accounts: r.noFollowUpCount,
            tone: r.noFollowUpCount > 0 ? 'warn' : 'neutral', to: book(worklistQuery('no_follow_up')),
        },
        {
            label: 'Marked urgent', accounts: r.urgentCount,
            tone: r.urgentCount > 0 ? 'dang' : 'neutral', to: book(worklistQuery('urgent')),
        },
        {
            label: 'Bad debt', sub: 'Operational classification only — the amount remains outstanding',
            accounts: r.badDebtCount, amount: r.badDebtAmount, to: book('rank=Bad'),
        },
    ];

    return (
        <>
            <div className="px-5 py-4 flex items-end justify-between gap-x-6 gap-y-2 flex-wrap border-b border-separator">
                <div>
                    <div className="text-caption font-medium text-label-3">Outstanding</div>
                    <div className="text-title font-semibold text-label num leading-tight mt-0.5">{money.exact(r.outstanding)}</div>
                </div>
                <Link to={book('')} className="text-secondary text-accent-text font-semibold hover:underline">
                    {count(r.withDuesCount)} {r.withDuesCount === 1 ? 'account' : 'accounts'} with dues →
                </Link>
            </div>
            <Coverage receivables={r} book={book} />
            <Table caption="The position now: how many accounts, and how much money, in each state.">
                <thead>
                    <tr>
                        <Th><span className="sr-only">State</span></Th>
                        <Th numeric className="w-24">Accounts</Th>
                        <Th numeric className="w-32">They owe</Th>
                    </tr>
                </thead>
                <tbody>
                    {lines.map(line => {
                        const accounts = count(line.accounts);
                        const amount = line.amount === undefined ? null : money.compact(line.amount);
                        const spoken = `${line.label}: ${accounts} ${line.accounts === 1 ? 'account' : 'accounts'}${amount ? `, ${amount}` : ''}`;
                        return (
                            <tr key={line.label}>
                                <RowHead className="font-normal">
                                    <div className="font-semibold text-label">
                                        {line.to ? (
                                            <Link to={line.to} aria-label={spoken} className="hover:text-accent-text hover:underline">
                                                {line.label}
                                            </Link>
                                        ) : line.label}
                                    </div>
                                    {line.sub && <div className="text-caption text-label-3 font-normal">{line.sub}</div>}
                                </RowHead>
                                <Td numeric className={cx('font-semibold', TONE[line.tone ?? 'neutral'])}>{accounts}</Td>
                                <Td
                                    numeric
                                    className={amount ? 'text-label-2' : 'text-label-3'}
                                    title={line.amount === undefined ? undefined : money.exact(line.amount)}
                                >
                                    {amount ?? '—'}
                                </Td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
            <p className="px-5 py-2.5 border-t border-separator text-caption text-label-3">
                They owe: the whole balance of those accounts — not an amount overdue.
            </p>
            {/* Not money owed, so not in that column: what was promised, and what is held for customers. */}
            <dl className="px-5 pb-1 border-t border-separator divide-y divide-separator">
                <Stat label="Promised" sub="Open promises — expected, not received" to={book('followUp=promise_open')}
                    value={<>{count(r.promisedCount)}{r.promisedAmount > 0 && <span className="text-label-3 font-normal"> · {money.compact(r.promisedAmount)}</span>}</>} />
                <Stat label="Held for customers" sub="Accounts in credit — theirs, not owed to you" to={book('settlement=credit')}
                    value={<>{count(r.creditCount)}{r.creditAmount > 0 && <span className="text-label-3 font-normal"> · {money.compact(r.creditAmount)}</span>}</>} />
            </dl>
        </>
    );
}

/* ---------------------------- where it concentrates ------------------------ */

/**
 * Where it concentrates (Customer 360, 02 §8): the five largest balances as a list of bars on one track, each with
 * its share of what is owed and the way into the account. One colour, because it is one measure; every figure is
 * written beside its bar.
 */
function Concentration({ largest, receivable, book, slug }: {
    largest: { id: string; company: string; amount: number }[];
    receivable: number;
    book: (query: string) => string;
    slug: string;
}) {
    const { money } = useFormatters();
    const together = concentration(largest, receivable);
    if (!together) return null;
    return (
        <Panel
            className="mt-6" title="Where it concentrates" description="The largest balances, and each one’s share of what is owed."
            actions={
                <p className="text-secondary text-label-3">
                    Together, {together.percent} of what is owed.{' '}
                    <Link to={book('settlement=owes&sort=amount-desc')} className="font-semibold text-accent-text hover:underline whitespace-nowrap">All accounts by amount</Link>
                </p>
            }
        >
            {/* Side by side, as many across as fit: five short bars on one track each, not five bars the page's width. */}
            <ul aria-label="The largest balances" className="px-5 pb-5 grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
                {largest.map(a => {
                    const share = receivable > 0 ? a.amount / receivable : 0;
                    return (
                        <li key={a.id} className="min-w-0">
                            <Link to={paths(slug).customer(a.id)} title={a.company} className="block truncate text-secondary font-semibold text-label hover:text-accent-text hover:underline">
                                {a.company}
                            </Link>
                            <span className="block text-secondary num whitespace-nowrap">
                                <span className="font-semibold text-label" title={money.exact(a.amount)}>{money.compact(a.amount)}</span>
                                <span className="text-label-3"> · {percentWords(share)}</span>
                            </span>
                            <ProportionBar share={share} label={`${a.company}: ${money.exact(a.amount)}, ${percentWords(share)} of what is owed`} className="mt-1.5 !h-1.5" />
                        </li>
                    );
                })}
            </ul>
        </Panel>
    );
}

/* -------------------------------- the ageing ------------------------------ */

/**
 * How old the money is.
 *
 * A table, not a chart. The figures are the point, they need to be read exactly,
 * and a bar somebody has to hover to read a number from is a worse version of
 * the number. The share bar sits in its own column, after the stated
 * percentage, never instead of it — and it is `aria-hidden`, because it says
 * nothing the row does not.
 *
 * Every row opens the accounts it counted: `filterValue` comes from the same
 * query that produced the count.
 */
function Ageing({
    rows: all, receivable, book, isPhone,
}: {
    rows: AgeingRow[];
    receivable: number;
    book: (query: string) => string;
    isPhone: boolean;
}) {
    const { money, count } = useFormatters();
    // A sheet's four bands, or a TallyPrime company's own windows — named as Settings names them (D3,
    // docs/tally/18 §1.3) — with what the windows leave beside them listed after, each opening its accounts.
    // "Not yet due: nothing" is true only where money is aged from its due date. Aged from the bill date nothing is ever
    // "not yet due", and the zero would say everything is (Customer 360, F5).
    const basis = all.find(r => r.layout)?.layout?.basis ?? null;
    const beside = besideRows(all).filter(r => r.amount !== 0 || (r.kind === 'not_yet_due' && basis === 'due_date'));
    const rows = agedRows(all);
    const windows = rows.some(r => r.kind === 'window');
    const total = rows.reduce((sum, row) => sum + row.amount, 0);

    if (total === 0 && beside.every(r => r.amount === 0)) {
        return (
            <EmptyState
                compact
                title="Nothing is ageing"
                hint="No account is carrying a debit balance in any age band."
            />
        );
    }

    const share = (amount: number) => (total > 0 ? Math.round((amount / total) * 100) : 0);
    // Open bills in the windows, plus what is beside them (credits counting down): the net receivable. Never a credit
    // in a window, so nothing is counted twice and nothing disappears (docs/tally/18 §4).
    const net = windows ? Math.round((total + beside.reduce((sum, r) => sum + (r.kind === 'credits' ? -r.amount : r.amount), 0)) * 100) / 100 : null;
    const label = (bucket: string) => ageingRowLabel(all.find(r => r.bucket === bucket)!, all);
    const signed = (row: AgeingRow) => (row.kind === 'credits' ? `− ${money.exact(row.amount)}` : money.exact(row.amount));

    if (isPhone) {
        return (
            <ul className="divide-y divide-separator">
                {rows.map(row => (
                    <li key={row.bucket}>
                        <Link
                            to={book(`ageing=${row.filterValue}&settlement=all`)}
                            className="block px-4 py-3 min-h-14 hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
                            aria-label={`${label(row.bucket)}: ${money.exact(row.amount)} across ${count(row.accountCount)} accounts`}
                        >
                            <div className="flex items-baseline justify-between gap-3">
                                <span className="text-body font-semibold text-label">{label(row.bucket)}</span>
                                <span className="text-body font-semibold text-label num">{money.compact(row.amount)}</span>
                            </div>
                            <div className="flex items-center justify-between gap-3 mt-1">
                                <span className="text-caption text-label-3">
                                    {count(row.accountCount)} {row.accountCount === 1 ? 'account' : 'accounts'} · {share(row.amount)}%
                                </span>
                                <ShareBar share={share(row.amount)} />
                            </div>
                        </Link>
                    </li>
                ))}
                {beside.map(row => (
                    <li key={row.bucket}>
                        <Link
                            to={book(`ageing=${row.filterValue}&settlement=all`)}
                            className="block px-4 py-3 min-h-14 hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
                            aria-label={`${label(row.bucket)}: ${signed(row)} across ${count(row.accountCount)} accounts`}
                        >
                            <div className="flex items-baseline justify-between gap-3">
                                <span className="text-body text-label-2">{label(row.bucket)}</span>
                                <span className="text-body text-label-2 num">{signed(row)}</span>
                            </div>
                        </Link>
                    </li>
                ))}
                {net !== null && beside.length > 0 && (
                    <li className="px-4 py-3 flex items-baseline justify-between gap-3" data-testid="ageing-net">
                        <span className="text-body font-semibold text-label">Net receivable</span>
                        <span className="text-body font-semibold text-label num whitespace-nowrap">{money.balance(Math.abs(net), net < 0 ? 'Cr' : 'Dr')}</span>
                    </li>
                )}
            </ul>
        );
    }

    return (
        <>
        <div className="px-5 pt-4 pb-3">
            <AgeingBar parts={rows.map(row => row.amount)} label={rows.map(row => `${label(row.bucket)} ${share(row.amount)}%`).join(', ')} />
        </div>
        <Table caption="Outstanding debit by age band, with the share of the total each band carries.">
            <thead>
                <tr>
                    <Th>Age</Th>
                    <Th numeric className="w-28">Amount</Th>
                    <Th numeric className="w-20">Accounts</Th>
                    {/* The bar joins the figure once the table has room for it; beside the position panel on a laptop, it has not. */}
                    <Th numeric className="w-18 @min-[36rem]:w-44">Share</Th>
                    <Th className="w-16"><span className="sr-only">Open</span></Th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row, i) => (
                    <tr key={row.bucket}>
                        <RowHead className="whitespace-nowrap">
                            <span aria-hidden="true" className={cx('inline-block h-2.5 w-2.5 rounded-full mr-2 align-baseline', ageSwatch(i, rows.length))} />
                            {label(row.bucket)}
                        </RowHead>
                        <Td numeric className="text-label">{money.exact(row.amount)}</Td>
                        <Td numeric className="text-label-2">{count(row.accountCount)}</Td>
                        <Td numeric className="text-label-2">
                            <span className="inline-flex items-center justify-end gap-3">
                                <ShareBar share={share(row.amount)} className="hidden @min-[36rem]:block" />
                                <span className="w-9">{share(row.amount)}%</span>
                            </span>
                        </Td>
                        <Td className="text-right">
                            <Link
                                to={book(`ageing=${row.filterValue}&settlement=all`)}
                                className="text-secondary text-accent-text font-semibold hover:underline"
                                aria-label={`Open the ${count(row.accountCount)} accounts with money ${label(row.bucket).toLowerCase()} old`}
                            >
                                Open
                            </Link>
                        </Td>
                    </tr>
                ))}
            </tbody>
            <tfoot>
                <tr className="border-t border-separator-strong">
                    <th scope="row" className="px-3 pl-5 h-11 text-left font-semibold text-label-2">{windows ? 'Open bills' : 'Total'}</th>
                    <td className="px-3 h-11 text-right num font-semibold text-label whitespace-nowrap">{money.exact(total)}</td>
                    <td colSpan={3} />
                </tr>
                {beside.map(row => (
                    <tr key={row.bucket}>
                        <th scope="row" className="px-3 pl-5 h-11 text-left font-normal text-label-2">{label(row.bucket)}</th>
                        <td className="px-3 h-11 text-right num text-label-2 whitespace-nowrap">{signed(row)}</td>
                        <td className="px-3 h-11 text-right num text-label-2">{count(row.accountCount)}</td>
                        <td />
                        <td className="px-3 pr-5 h-11 text-right">
                            <Link to={book(`ageing=${row.filterValue}&settlement=all`)} className="text-secondary text-accent-text font-semibold hover:underline"
                                aria-label={`Open the ${count(row.accountCount)} accounts: ${label(row.bucket).toLowerCase()}`}>
                                Open
                            </Link>
                        </td>
                    </tr>
                ))}
                {net !== null && beside.length > 0 && (
                    <tr className="border-t border-separator-strong" data-testid="ageing-net">
                        <th scope="row" className="px-3 pl-5 h-11 text-left font-semibold text-label">Net receivable</th>
                        <td className="px-3 h-11 text-right num font-semibold text-label whitespace-nowrap">{money.balance(Math.abs(net), net < 0 ? 'Cr' : 'Dr')}</td>
                        <td colSpan={3} />
                    </tr>
                )}
            </tfoot>
        </Table>
        {/* What the figures are, said once under the table in a full line — not squeezed right-aligned into the
            spare cells of a total row, three lines deep (2026-10 global sweep). */}
        {(windows || total !== receivable) && (
            <p className="px-5 py-3 border-t border-separator text-caption text-label-3 leading-relaxed">
                {windows
                    ? <>As TallyPrime’s Age-wise report: open bills only, with what is not in them listed beneath.{net !== null && beside.length > 0 && ' The net receivable is the open bills and what is beside them, less the credits.'}</>
                    : 'Bands may total less than the receivable when a sheet supplies its own roll-ups.'}
            </p>
        )}
        </>
    );
}

/** A share of the total, drawn on a fixed track so the bars start and end in line. */
const ShareBar = ({ share, className }: { share: number; className?: string }) => (
    <span aria-hidden="true" className={cx('block w-24 h-1.5 rounded-full bg-card-3 overflow-hidden', className)}>
        <span className="block h-full rounded-full bg-accent" style={{ width: `${Math.max(2, share)}%` }} />
    </span>
);

/* ------------------------------- the activity ----------------------------- */

/** One line of what was done: a label, perhaps a note, and its figure, right-aligned. */
function Stat({
    label, sub, value, tone = 'neutral', to, mark,
}: {
    label: string;
    sub?: string;
    value: React.ReactNode;
    tone?: Tone;
    to?: string;
    /** The colour of this line's part of a bar above it, as a square before the label (never the colour alone). */
    mark?: string;
}) {
    return (
        <div className="flex items-center justify-between gap-4 min-h-11 py-2">
            <dt className="min-w-0">
                {mark && <span aria-hidden="true" className={cx('inline-block size-2 mr-2 align-[1px]', mark)} />}
                <span className="text-body font-medium text-label">
                    {to ? <Link to={to} className="hover:text-accent-text hover:underline">{label}</Link> : label}
                </span>
                {sub && <span className="block text-caption text-label-3">{sub}</span>}
            </dt>
            <dd className={cx('text-body font-semibold num text-right whitespace-nowrap', TONE[tone])}>{value}</dd>
        </div>
    );
}

/**
 * What was done in the period, in two short lists — the conversations, and
 * what they came to — instead of eight cards of equal weight.
 */
function Activity({ activity, slug, promises, awaiting, book }: {
    activity: ActivityTotals | null;
    slug: string;
    promises: PromiseOutcomes | null;
    awaiting: { count: number; amount: number } | null;
    book: (query: string) => string;
}) {
    const { money, count } = useFormatters();

    if (!activity) {
        return (
            <p className="px-5 py-4 text-secondary text-label-3">We could not read the activity for this period.</p>
        );
    }

    if (activity.followUps === 0 && activity.chequesCleared === 0 && activity.accountsSettled === 0) {
        return (
            <EmptyState
                compact
                title="Nothing was recorded in this period"
                hint="No follow-ups, no cheques cleared and no accounts settled between these dates. Try a longer period."
            />
        );
    }

    const withAmount = (n: number, amount: number) => (
        <>{count(n)}{amount > 0 && <span className="text-label-3 font-normal"> · {money.compact(amount)}</span>}</>
    );

    return (
        // Three groups side by side where there is room — contact, money, promises answered — with space between
        // them rather than a vertical rule that ran on past the shorter column (2026-10 global sweep).
        <div className="grid gap-x-10 px-5 md:grid-cols-2 xl:grid-cols-3">
            <section aria-labelledby="activity-contact" className="pt-3 pb-2">
                <h3 id="activity-contact" className="text-caption font-semibold text-label-3">Contact</h3>
                <dl className="divide-y divide-separator">
                    <Stat label="Follow-ups recorded" value={count(activity.followUps)} />
                    <Stat label="Accounts contacted" sub="Distinct customers, not calls" value={count(activity.accountsTouched)} />
                    <Stat label="Calls that reached somebody" value={count(activity.callsAnswered)} />
                    <Stat label="No answer or refused" value={count(activity.callsUnanswered)} />
                    <Stat
                        label="Disputes raised"
                        value={count(activity.disputes)}
                        tone={activity.disputes > 0 ? 'warn' : 'neutral'}
                    />
                </dl>
            </section>
            <section aria-labelledby="activity-money" className="pt-3 pb-2 max-md:border-t max-md:border-separator">
                <h3 id="activity-money" className="text-caption font-semibold text-label-3">Money</h3>
                <dl className="divide-y divide-separator">
                    <Stat
                        label="Promises taken"
                        sub="Expected, not received"
                        value={withAmount(activity.promises, activity.promisedAmount)}
                    />
                    <Stat
                        label="Cheques cleared"
                        value={withAmount(activity.chequesCleared, activity.chequesClearedAmount)}
                        tone={activity.chequesCleared > 0 ? 'pos' : 'neutral'}
                        to={`${paths(slug).cheques}?state=cleared`}
                    />
                    <Stat
                        label="Accounts settled"
                        sub="When a sync first saw the balance clear"
                        value={count(activity.accountsSettled)}
                    />
                </dl>
            </section>
            {promises && (
                <section aria-labelledby="activity-promises" className="pt-3 pb-2 border-t border-separator md:col-span-2 xl:col-span-1 xl:border-t-0">
                    <h3 id="activity-promises" className="text-caption font-semibold text-label-3">Promise outcomes</h3>
                    {/* One bar, three parts, counted: kept (jade), waiting for an answer (amber), not kept (crimson). */}
                    {promises.keptCount + promises.notKeptCount + (awaiting?.count ?? 0) > 0 && (
                        <SegmentBar
                            className="mt-2.5 mb-1"
                            label={`Kept ${count(promises.keptCount)}${awaiting ? `, date passed and not answered ${count(awaiting.count)}` : ''}, not kept ${count(promises.notKeptCount)}`}
                            parts={[
                                { key: 'kept', amount: promises.keptCount, fill: 'bg-promise-kept' },
                                { key: 'waiting', amount: awaiting?.count ?? 0, fill: 'bg-promise-waiting' },
                                { key: 'broken', amount: promises.notKeptCount, fill: 'bg-promise-broken' },
                            ]}
                        />
                    )}
                    <dl className="grid md:grid-cols-3 md:gap-x-8 xl:grid-cols-1 divide-y md:divide-y-0 xl:divide-y divide-separator">
                        <Stat label="Kept" mark="bg-promise-kept" value={withAmount(promises.keptCount, promises.keptAmount)}
                            tone={promises.keptCount > 0 ? 'pos' : 'neutral'} />
                        {awaiting && (
                            <Stat label="Date passed, not answered" sub="Now, whatever the period" mark="bg-promise-waiting"
                                value={withAmount(awaiting.count, 0)}
                                tone={awaiting.count > 0 ? 'warn' : 'neutral'} to={book('followUp=promise_passed')} />
                        )}
                        <Stat label="Not kept" mark="bg-promise-broken" value={withAmount(promises.notKeptCount, promises.notKeptAmount)}
                            tone={promises.notKeptCount > 0 ? 'dang' : 'neutral'} to={book('followUp=promise_not_kept')} />
                    </dl>
                    <p className="mt-1 text-caption text-label-3 leading-relaxed">Kept and not kept: answered in this period, as recorded by your people. The amounts were promised, not received — Timely does not see the bank.</p>
                </section>
            )}
        </div>
    );
}

/**
 * How much of the book has a plan — the proportion of two figures above, never
 * a score — with the rest one press away.
 */
function Coverage({ receivables: r, book }: { receivables: Receivables; book: (query: string) => string }) {
    const { count } = useFormatters();
    // Counted by the database over the accounts not marked as bad debt, the queue's own rule.
    const cover = r.planBaseCount > 0
        ? { planned: r.plannedCount, total: r.planBaseCount, share: r.plannedCount / r.planBaseCount }
        : followUpCoverage({ total: 0, overdue: 0, noFollowUp: 0 });
    if (cover.share === null) return null;
    const percent = Math.round(cover.share * 100);
    return (
        <div className="px-5 py-3 border-b border-separator">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <span className="text-secondary font-semibold text-label">Follow-up coverage</span>
                <span className="text-secondary text-label-2">
                    <strong className="font-semibold text-label num">{percent}%</strong>
                    <span className="text-label-3"> · {count(cover.planned)} of {count(cover.total)} accounts have a plan</span>
                </span>
            </div>
            <ProportionBar
                share={cover.share}
                tone={cover.share >= 0.8 ? 'pos' : cover.share >= 0.5 ? 'warn' : 'dang'}
                label={`Follow-up coverage ${percent}%: ${cover.planned} of ${cover.total} accounts that owe and are not marked as bad debt have a next follow-up still to come, or are marked collected`}
                className="mt-2"
            />
            {cover.total > cover.planned && (
                <Link to={book(UNPLANNED_QUERY)} className="inline-block mt-2 text-caption text-accent-text font-semibold hover:underline">
                    Open the {count(cover.total - cover.planned)} late or undated
                </Link>
            )}
        </div>
    );
}

/* --------------------------------- the team ------------------------------- */

function Team({
    team, unavailable, book, isPhone,
}: {
    team: MemberReport[];
    unavailable: boolean;
    book: (query: string) => string;
    isPhone: boolean;
}) {
    const { money, count } = useFormatters();

    const heading = (
        <div className="px-5 pt-4 pb-3 border-t border-separator">
            <h3 className="text-body font-semibold text-label">By person</h3>
            <p className="text-caption text-label-3 mt-0.5">
                What each person is carrying now, and — right of the rule — what they recorded in the period.
            </p>
        </div>
    );

    if (unavailable) {
        return (
            <>
                {heading}
                <p className="px-5 pb-4 text-secondary text-label-3">We could not read the team figures just now.</p>
            </>
        );
    }

    if (team.length === 0) return null;

    if (isPhone) {
        return (
            <>
                {heading}
                <ul className="divide-y divide-separator border-t border-separator">
                    {team.map(member => (
                        <li key={member.memberId} className="px-4 py-3">
                            <div className="flex items-baseline justify-between gap-3">
                                <Link
                                    to={book(`owner=${member.memberId}`)}
                                    className="text-body font-semibold text-label hover:text-accent-text hover:underline"
                                >
                                    {member.displayName}
                                </Link>
                                <span className="text-body font-semibold text-label num">
                                    {money.compact(member.assignedAmount)}
                                </span>
                            </div>
                            <div className="text-caption text-label-3 mt-1">
                                {count(member.assignedCount)} accounts ·{' '}
                                <span className={member.overdueCount > 0 ? 'text-warn font-semibold' : ''}>
                                    {count(member.overdueCount)} late
                                </span>{' '}
                                · {count(member.noFollowUpCount)} unplanned
                            </div>
                            <div className="text-caption text-label-3 mt-0.5">
                                In the period: {count(member.followUps)} follow-ups,{' '}
                                {count(member.promises)} promises
                            </div>
                        </li>
                    ))}
                </ul>
            </>
        );
    }

    return (
        <>
            {heading}
            <Table caption="Each person’s assigned accounts now, and what they recorded during the selected period.">
                <thead>
                    <tr>
                        <Th>Person</Th>
                        <Th numeric className="w-24">Accounts</Th>
                        <Th numeric className="w-32">Assigned</Th>
                        <Th numeric className="w-24">Late</Th>
                        <Th numeric className="w-24">Unplanned</Th>
                        <Th className="w-36">Coverage</Th>
                        <Th numeric className="w-28 border-l border-separator">Follow-ups</Th>
                        <Th numeric className="w-40">Promises</Th>
                    </tr>
                </thead>
                <tbody>
                    {team.map(member => (
                        <tr key={member.memberId}>
                            <RowHead>
                                <Link to={book(`owner=${member.memberId}`)} className="hover:text-accent-text hover:underline">
                                    {member.displayName}
                                </Link>
                            </RowHead>
                            <Td numeric className="text-label-2">{count(member.assignedCount)}</Td>
                            <Td numeric className="text-label">{money.compact(member.assignedAmount)}</Td>
                            <Td numeric className={member.overdueCount > 0 ? 'text-warn font-semibold' : 'text-label-3'}>
                                {count(member.overdueCount)}
                            </Td>
                            <Td numeric className="text-label-2">{count(member.noFollowUpCount)}</Td>
                            <Td><MemberCoverage member={member} /></Td>
                            <Td numeric className="text-label-2 border-l border-separator">{count(member.followUps)}</Td>
                            <Td numeric className="text-label-2">
                                {count(member.promises)}
                                {member.promisedAmount > 0 && (
                                    <span className="text-label-3"> · {money.compact(member.promisedAmount)}</span>
                                )}
                            </Td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </>
    );
}

/** One person's coverage: their accounts with a plan, over their accounts. */
function MemberCoverage({ member }: { member: MemberReport }) {
    const cover = followUpCoverage({
        total: member.assignedCount, overdue: member.overdueCount,
        noFollowUp: member.noFollowUpCount, writtenOff: member.writtenOffCount,
    });
    if (cover.share === null) return <span className="text-secondary text-label-3">—</span>;
    const percent = Math.round(cover.share * 100);
    return (
        <span className="flex items-center gap-2">
            <ProportionBar
                share={cover.share}
                tone={cover.share >= 0.8 ? 'pos' : cover.share >= 0.5 ? 'warn' : 'dang'}
                label={`${member.displayName}: ${cover.planned} of ${cover.total} accounts have a plan`}
                className="flex-1 min-w-12"
            />
            <span className="text-secondary text-label-2 num w-9 text-right">{percent}%</span>
        </span>
    );
}
